import { Skeleton } from '@/components/ui/skeleton';
import { useLineConsumptionSummary } from '@/hooks/useLines';
import { fadeUp } from '@/lib/animations';
import type { MobileLine } from '@octave/contracts';
import { motion } from 'framer-motion';
import { ArrowRight, Image, MessageSquare, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { deriveConsumptionStats } from './consumption';

interface LineConsumptionTabProps {
  lineId: string | undefined;
  lineDetails: MobileLine;
  tenantSlug: string | undefined;
}

/** Current-month consumption cards (data gauge, voice, SMS, MMS). */
export function LineConsumptionTab({
  lineId,
  lineDetails,
  tenantSlug,
}: LineConsumptionTabProps) {
  const currentDate = new Date();
  const {
    data: consumptionData = [],
    isLoading,
    error,
  } = useLineConsumptionSummary(
    lineId,
    currentDate.getMonth() + 1,
    currentDate.getFullYear(),
  );

  const stats = deriveConsumptionStats(
    consumptionData,
    lineDetails.phonePlan.dataLimitMo,
  );

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className="rounded-2xl bg-white p-6 shadow-sm"
    >
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Consommation</h2>
          <p className="mt-0.5 text-sm text-slate-400">
            Utilisation du mois en cours
          </p>
        </div>
        <Link
          to={`/o/${tenantSlug}/analytics?lineId=${lineId}`}
          className="text-xs font-medium text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
        >
          Analyse détaillée <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : error ? (
        <p className="text-sm text-slate-400 py-8 text-center">
          Données de consommation indisponibles pour le moment
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {lineDetails.phonePlan.dataLimitMo > 0 && (
            <div className="flex flex-col items-center rounded-xl bg-slate-50 p-4">
              <div className="relative h-24 w-24">
                <svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
                  <circle
                    strokeWidth="10"
                    stroke="#f1f5f9"
                    fill="transparent"
                    r="40"
                    cx="50"
                    cy="50"
                  />
                  <circle
                    strokeWidth="10"
                    strokeLinecap="round"
                    stroke="#10b981"
                    fill="transparent"
                    r="40"
                    cx="50"
                    cy="50"
                    strokeDasharray={`${Math.min(stats.dataPercentage * 2.512, 251.2)} 251.2`}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-xl font-bold text-slate-900">
                    {Math.round(stats.dataPercentage)}%
                  </span>
                </div>
              </div>
              <p className="mt-2 text-xs text-slate-400">
                {stats.dataUsedGo} Go / {stats.dataTotalGo} Go
              </p>
            </div>
          )}

          {!lineDetails.phonePlan.isDataOnly && (
            <>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50">
                  <Phone className="h-4 w-4 text-emerald-600" />
                </div>
                <p className="mt-3 text-2xl font-bold text-slate-900">
                  {stats.voiceDuration.value}
                </p>
                <p className="text-xs text-slate-400">
                  {stats.voiceDuration.label}
                </p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-50">
                  <MessageSquare className="h-4 w-4 text-sky-600" />
                </div>
                <p className="mt-3 text-2xl font-bold text-slate-900">
                  {stats.smsCount}
                </p>
                <p className="text-xs text-slate-400">SMS envoyés</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-50">
                  <Image className="h-4 w-4 text-rose-600" />
                </div>
                <p className="mt-3 text-2xl font-bold text-slate-900">
                  {stats.mmsCount}
                </p>
                <p className="text-xs text-slate-400">MMS envoyés</p>
              </div>
            </>
          )}
        </div>
      )}
    </motion.div>
  );
}
